import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import { Loader2 } from "lucide-react";

import {
  agentModelsQueryOptions,
  agentSettingsQueryOptions,
  defaultChoices,
  saveDefaultModel,
  saveDesktopContext,
} from "@/agent/chat/models";
import { ModelName } from "@/agent/chat/ProviderMark";
import { handleApiError } from "@/services/api";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Switch } from "@/components/ui/switch";

/**
 * /profile: the model the user's chats run on when a chat has no pick of its own (WhatsApp too),
 * and whether WhatsApp and Telegram see what they had open on their computer (saved on toggle).
 */
const AssistantModelCard = () => {
  const queryClient = useQueryClient();
  const models = useQuery(agentModelsQueryOptions);
  const catalog = models.data;
  const settings = useQuery(agentSettingsQueryOptions);
  const userSettings = settings.data;
  const [choice, setChoice] = useState<string | null>(null);
  const saved = userSettings?.default_model_ref;
  const selected = choice ?? saved;

  const mutation = useMutation({
    mutationFn: saveDefaultModel,
    onSuccess: (data) => {
      queryClient.setQueryData(agentSettingsQueryOptions.queryKey, data);
      setChoice(null);
      void queryClient.invalidateQueries({ queryKey: agentModelsQueryOptions.queryKey });
    },
  });
  const desktopMutation = useMutation({
    mutationFn: saveDesktopContext,
    onSuccess: (data) => queryClient.setQueryData(agentSettingsQueryOptions.queryKey, data),
  });
  const orgSharesDesktop = userSettings?.desktop_context_org ?? true;
  const saveError = mutation.error ?? desktopMutation.error;

  // The card shows from the start: a spinner while it loads, the error with Try again if it
  // can't (after one quick retry), then the choice.
  const loaded = !!catalog && !!userSettings;
  const fetching = models.isFetching || settings.isFetching;
  const loadFailed = !loaded && (models.isError || settings.isError) && !fetching;
  const retry = () => {
    if (models.isError) void models.refetch();
    if (settings.isError) void settings.refetch();
  };
  const busy = !loaded || mutation.isPending;
  return (
    <Card className="md:col-span-2">
      <CardHeader>
        <CardTitle>Assistant</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {saveError && (
          <Alert variant="destructive">
            <AlertDescription>{handleApiError(saveError as AxiosError)}</AlertDescription>
          </Alert>
        )}
        {loadFailed && (
          <Alert variant="destructive" className="flex items-center gap-3">
            <AlertDescription>Your assistant settings didn't load.</AlertDescription>
            <Button variant="outline" size="sm" className="ml-auto max-sm:h-10" onClick={retry}>
              Try again
            </Button>
          </Alert>
        )}
        <div className="space-y-3">
          <Label id="assistant-default-model">Default model</Label>
          {loaded ? (
            <RadioGroup
              aria-labelledby="assistant-default-model"
              value={selected}
              onValueChange={setChoice}
              disabled={busy}
            >
              {defaultChoices(catalog).map((model) => (
                // A bordered row per model, the chosen one outlined and tinted (05c / 05f).
                <label
                  key={model.ref}
                  className="flex w-full max-w-[420px] cursor-pointer items-center gap-2.5 rounded-lg border px-3 py-2.5 text-sm transition-colors has-[[data-state=checked]]:border-primary has-[[data-state=checked]]:bg-accent/50"
                >
                  <RadioGroupItem value={model.ref} />
                  <ModelName model={model} />
                </label>
              ))}
            </RadioGroup>
          ) : (
            !loadFailed && (
              <Loader2
                className="h-4 w-4 text-muted-foreground motion-safe:animate-spin"
                aria-label="Loading"
              />
            )
          )}
        </div>
        <Button
          onClick={() => selected && mutation.mutate(selected)}
          disabled={busy || !selected || selected === saved}
        >
          {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
          Save
        </Button>
        {loaded && (
          <div className="flex items-center justify-between gap-4 border-t pt-4">
            <div className="space-y-0.5 pr-4">
              <Label htmlFor="assistant-desktop-context">
                Share desktop context with WhatsApp/Telegram
              </Label>
              <p className="text-xs text-muted-foreground">
                {orgSharesDesktop
                  ? "When you message the assistant on WhatsApp or Telegram, it knows which applications you had open on your computer, so it can tell which one you mean."
                  : "Your organization has turned this off."}
              </p>
            </div>
            <Switch
              id="assistant-desktop-context"
              checked={orgSharesDesktop && userSettings.desktop_context !== false}
              onCheckedChange={(on) => desktopMutation.mutate(on)}
              disabled={!orgSharesDesktop || desktopMutation.isPending}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AssistantModelCard;
